import { AccessController } from '@adornis/abac/core/AccessController.js';
import type { Request } from '@adornis/abac/core/Request.js';
import type { IPayload, IPolicy } from '@adornis/abac/core/types.js';
import './api/index.js';
import { type AdornisContext, type ClientContext, context } from '@adornis/baseql/server/context.js';
import './entities/index.js';
import { createPermissionPayload, SimpleRolePolicy } from '@adornis/abac/policies/SimpleRolePolicy.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { LMSCourseEnrollment } from './entities/LMSCourseEnrollment.js';

// ─── LMS Actions ────────────────────────────────────────────────────────────

export enum LMSAction {
  // User-scoped
  ViewCourse = 'ViewCourse',
  ViewCoursePages = 'ViewCoursePages',
  SubmitAnswer = 'SubmitAnswer',
  CompleteAttempt = 'CompleteAttempt',
  CreateAttempt = 'CreateAttempt',

  // Admin-scoped
  CourseCreate = 'CourseCreate',
  CourseRead = 'CourseRead',
  CourseUpdate = 'CourseUpdate',
  CourseDelete = 'CourseDelete',
  CourseEnrollmentCreate = 'CourseEnrollmentCreate',
  CourseEnrollmentRead = 'CourseEnrollmentRead',
  CourseEnrollmentUpdate = 'CourseEnrollmentUpdate',
  CourseEnrollmentDelete = 'CourseEnrollmentDelete',
  FileManage = 'FileManage',
}

// ─── LMS Payload ────────────────────────────────────────────────────────────

export const LMS_PAYLOAD_TYPE = '@nsc/simple-lms';

export interface ILMSPayload extends IPayload {
  type: typeof LMS_PAYLOAD_TYPE;
  action: LMSAction;
  userId?: string;
  enrollmentUserId?: string;
  enrollmentId?: string;
  courseId?: string;
}

export const createLMSPayload = (params: Omit<ILMSPayload, 'type'>): ILMSPayload => ({
  type: LMS_PAYLOAD_TYPE,
  ...params,
});

export const isLMSPayload = (payload: IPayload): payload is ILMSPayload => payload.type === LMS_PAYLOAD_TYPE;

// --- Default Controller ------------------------

const ENROLLMENT_OWNER_ACTIONS = new Set<LMSAction>([
  LMSAction.SubmitAnswer,
  LMSAction.CreateAttempt,
  LMSAction.CompleteAttempt,
]);

const AUTH_ONLY_ACTIONS = new Set<LMSAction>([LMSAction.ViewCourse, LMSAction.ViewCoursePages]);

export const DefaultLMSPolicy = {
  name: 'DefaultLMSPolicy',

  async evaluate(request: Request) {
    if (!isLMSPayload(request.payload)) return false;

    if ((context as AdornisContext).serverContext) {
      return { allowed: true, reason: 'server may do everything' };
    }

    const {
      action,
      userId: currentUserId = (context as ClientContext).userID,
      enrollmentId,
      enrollmentUserId = (
        await (
          await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName)
        ).findOne({
          _id: enrollmentId ?? `${Math.random()}`,
        })
      )?.userId,
      courseId,
    } = request.payload;

    // check global permissions. I don't want to use simple role policy for contextual policy queries, that we will do here
    const simpleRolePolicyOpinion = await SimpleRolePolicy.evaluate({ payload: createPermissionPayload(action) });
    if (
      (typeof simpleRolePolicyOpinion === 'boolean' && simpleRolePolicyOpinion) ||
      (typeof simpleRolePolicyOpinion === 'object' && simpleRolePolicyOpinion?.allowed)
    ) {
      return { allowed: true, reason: 'simple role policy said yes' };
    }

    // Users can read their own enrollments
    if (action === LMSAction.CourseEnrollmentRead && enrollmentUserId) {
      if (!currentUserId) return { allowed: false, reason: 'not authenticated' };
      return currentUserId === enrollmentUserId
        ? { allowed: true, reason: 'user reading own enrollment' }
        : { allowed: false, reason: 'user cannot read other enrollments' };
    }

    // Enrolled users can view course and pages
    if (action === LMSAction.ViewCourse || action === LMSAction.ViewCoursePages) {
      if (!courseId)
        return { allowed: false, reason: 'general access to all courses can be granted through simple role policy' };
      if (!currentUserId) return { allowed: false, reason: 'not authenticated' };

      // For specific course/page access, require courseId and check enrollment for that course
      const enrollmentCol = await getRawCollection<LMSCourseEnrollment>(LMSCourseEnrollment._collectionName);
      const hasEnrollment = await enrollmentCol.findOne({ userId: currentUserId, courseId });
      return hasEnrollment
        ? { allowed: true, reason: 'enrolled in course' }
        : { allowed: false, reason: 'user not enrolled in this course' };
    }

    if (AUTH_ONLY_ACTIONS.has(action)) {
      return currentUserId
        ? { allowed: true, reason: 'authenticated user' }
        : { allowed: false, reason: 'not authenticated' };
    }

    if (ENROLLMENT_OWNER_ACTIONS.has(action)) {
      if (!currentUserId) return { allowed: false, reason: 'not authenticated' };
      if (!enrollmentUserId) return { allowed: false, reason: 'enrollment context missing' };
      return currentUserId === enrollmentUserId
        ? { allowed: true, reason: 'enrollment owner' }
        : { allowed: false, reason: 'user does not own this enrollment' };
    }

    return false;
  },
};

// ─── Module init ────────────────────────────────────────────────────────────

let lmsAccessController: AccessController | null = null;

export interface InitLMSOptions {
  policies: IPolicy[];
}

/**
 * Initialize the LMS module with externally provided policies.
 * Must be called by the entrypoint before any LMS operations are invoked.
 */
export function initLMS(options: InitLMSOptions): void {
  lmsAccessController = new AccessController({ policies: options.policies ? options.policies : [DefaultLMSPolicy] });
}

export function getABAC(): AccessController {
  if (!lmsAccessController) throw new Error('LMS module not initialized — call initLMS() first');
  return lmsAccessController;
}
